/**
 * LootSystem — Loot table rolling, enemy loot mapping, and party loot distribution.
 * All loot tables are JSON-driven from loot_tables.json.
 *
 * @module LootSystem
 */

import DebugLog from '../debug/DebugLog.js';
import EventBus from '../core/EventBus.js';
import LOOT_TABLES_DATA from '../data/loot_tables.json';
import { createRNG } from '../utils/RNG.js';

// ─── Enemy → loot table prefix map ───────────────────────────────────────────

const ENEMY_TYPE_MAP = [
  { prefix: 'goblin',   tableId: 'goblin_loot' },
  { prefix: 'orc',      tableId: 'orc_loot' },
  { prefix: 'undead',   tableId: 'undead_loot' },
  { prefix: 'skeleton', tableId: 'undead_loot' },
  { prefix: 'zombie',   tableId: 'undead_loot' },
  { prefix: 'draugr',   tableId: 'undead_loot' },
  { prefix: 'troll',    tableId: 'troll_loot' },
  { prefix: 'bandit',   tableId: 'bandit_loot' },
  { prefix: 'boss',     tableId: 'boss_loot' },
];

const DEFAULT_TABLE = 'goblin_loot';

// ─── LootSystem ───────────────────────────────────────────────────────────────

const LootSystem = {
  /** @type {Map<string, object>} Loot tables keyed by id */
  _tables: new Map(),

  /** @type {boolean} */
  _ready: false,

  // ── Init ──────────────────────────────────────────────────────────────────

  /**
   * Load loot table definitions. Call once at startup.
   */
  init() {
    this._tables = new Map();
    for (const table of LOOT_TABLES_DATA) {
      this._tables.set(table.id, table);
    }
    this._ready = true;
    DebugLog.info(`[loot] LootSystem initialized; tables=${this._tables.size}`);
  },

  // ── Table access ──────────────────────────────────────────────────────────

  /**
   * Get a loot table by id.
   * @param {string} tableId
   * @returns {object|null}
   */
  getTable(tableId) {
    const table = this._tables.get(tableId);
    if (!table) {
      DebugLog.warn(`[loot] Unknown loot table: ${tableId}`);
      return null;
    }
    return table;
  },

  // ── Rolling ───────────────────────────────────────────────────────────────

  /**
   * Roll a loot table and return an array of loot results.
   * Each result is either { item_id, count } or { gold: N }.
   *
   * @param {string} tableId
   * @param {ReturnType<import('../utils/RNG.js').createRNG>} [rng]
   * @returns {Array<{item_id: string, count: number}|{gold: number}>}
   */
  rollTable(tableId, rng = null) {
    const table = this.getTable(tableId);
    if (!table) return [];

    const r = rng ?? createRNG(Date.now());
    const results = [];

    // Roll each item entry
    for (const entry of (table.entries ?? [])) {
      if (r.nextFloat() < entry.chance) {
        const count = r.nextInt(entry.minCount ?? 1, entry.maxCount ?? 1);
        results.push({ item_id: entry.item_id, count });
      }
    }

    // Roll gold
    if (table.gold) {
      const { chance, goldMin, goldMax } = table.gold;
      if (r.nextFloat() < chance) {
        const gold = r.nextInt(goldMin ?? 0, goldMax ?? 0);
        if (gold > 0) results.push({ gold });
      }
    }

    return results;
  },

  // ── Enemy → table mapping ─────────────────────────────────────────────────

  /**
   * Return the loot table id appropriate for the given enemy id.
   * Matches by prefix (e.g. goblin_scout → goblin_loot).
   *
   * @param {string} enemyId
   * @returns {string} table id
   */
  getLootForEnemy(enemyId) {
    if (!enemyId) return DEFAULT_TABLE;
    const id = enemyId.toLowerCase();

    // Check for boss suffix or prefix first
    if (id.includes('boss') || id.endsWith('_chief') || id.endsWith('_warlord') || id.endsWith('_lord')) {
      return 'boss_loot';
    }

    for (const { prefix, tableId } of ENEMY_TYPE_MAP) {
      if (id.startsWith(prefix)) return tableId;
    }

    return DEFAULT_TABLE;
  },

  // ── Award loot to party ───────────────────────────────────────────────────

  /**
   * Roll a loot table and apply results to the party: items → inventory, gold → partyGold.
   * Emits `loot:awarded` on EventBus with the full result list.
   *
   * @param {object} party — WorldState or object with partyGold + optional inventoryManager
   * @param {string} tableId
   * @param {ReturnType<import('../utils/RNG.js').createRNG>} [rng]
   * @returns {Array<{item_id: string, count: number}|{gold: number}>} rolled results
   */
  awardLoot(party, tableId, rng = null) {
    const results = this.rollTable(tableId, rng);
    if (!results.length) {
      DebugLog.info(`[loot] awardLoot; table=${tableId} no items rolled`);
      return results;
    }

    let goldTotal = 0;
    const itemResults = [];

    for (const result of results) {
      if ('gold' in result) {
        goldTotal += result.gold;
      } else {
        itemResults.push(result);
      }
    }

    // Apply gold
    if (goldTotal > 0 && party != null) {
      if (typeof party.partyGold === 'number') {
        party.partyGold += goldTotal;
      }
      DebugLog.info(`[loot] Awarded ${goldTotal} gold; new total=${party?.partyGold ?? '?'}`);
    }

    // Apply items via inventoryManager if available
    for (const { item_id, count } of itemResults) {
      if (typeof window !== 'undefined' && window.__inventoryManager?.addItem) {
        for (let i = 0; i < count; i++) {
          window.__inventoryManager.addItem(null, item_id, 1);
        }
      }
      DebugLog.info(`[loot] Awarded item; id=${item_id} count=${count}`);
    }

    EventBus.emit('loot:awarded', { tableId, results, gold: goldTotal, items: itemResults });

    DebugLog.event(
      `[loot] awardLoot complete; table=${tableId} items=${itemResults.length} gold=${goldTotal}`
    );

    return results;
  },
};

// ─── Debug exposure ───────────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  window.__debug = window.__debug ?? {};

  /**
   * Print what would be rolled from a loot table without applying it.
   * @param {string} tableId
   * @returns {Array<object>}
   */
  window.__debug.rollLoot = function rollLoot(tableId) {
    const results = LootSystem.rollTable(tableId);
    // eslint-disable-next-line no-console
    console.log(`[LootSystem] rollLoot('${tableId}'):`, JSON.stringify(results, null, 2));
    return results;
  };
}

export default LootSystem;
